import React, { useCallback, useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db as firestoreDb } from "../../../service/firebase";
import {
  addJogador,
  addJogadorAosReservas,
  getJogadores,
  getProximoIdJogador,
  deleteJogador,
  substituirJogadorNoDB,
} from "../../../db/db"; // Importa as funções
import "./ListaReservas.css";

const ListaReservas = ({ reservasAtualizados, onReservasAtualizadas, turmaId }) => {
  const [nomeJogador, setNomeJogador] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalSubOpen, setIsModalSubOpen] = useState(false);
  const [jogadorSelecionado, setJogadorSelecionado] = useState(null);
  const [membrosDisponiveis, setMembrosDisponiveis] = useState([]);
  const [carregandoMembros, setCarregandoMembros] = useState(false);
  const [erroMembros, setErroMembros] = useState("");
  const [adicionandoMembroId, setAdicionandoMembroId] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const getNomePreferencial = (usuario, fallback = "Jogador") => {
    if (!usuario) return fallback;
    return (
      usuario.nickname ||
      usuario.displayName ||
      usuario.nome ||
      usuario.email ||
      fallback
    );
  };

  const carregarMembrosDisponiveis = useCallback(async () => {
    if (!turmaId) return;
    setCarregandoMembros(true);
    setErroMembros("");
    try {
      const jogadoresAtuais = await getJogadores();
      const membershipsEmUso = new Set(
        jogadoresAtuais
          .map((jogador) => jogador.id_membro_turma)
          .filter(Boolean)
      );
      const usuariosEmUso = new Set(
        jogadoresAtuais.map((jogador) => jogador.id_usuario).filter(Boolean)
      );

      const membrosSnapshot = await getDocs(
        query(
          collection(firestoreDb, "jogador_turma"),
          where("id_turma", "==", turmaId),
          where("status", "==", 1)
        )
      );

      const membros = await Promise.all(
        membrosSnapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();
          let usuarioData = null;
          if (data?.id_usuario) {
            try {
              const usuarioDoc = await getDoc(
                doc(firestoreDb, "usuarios", data.id_usuario)
              );
              usuarioData = usuarioDoc.exists() ? usuarioDoc.data() : null;
            } catch (error) {
              console.error("Erro ao carregar usuário da turma", error);
            }
          }

          const nome = getNomePreferencial(usuarioData, "Jogador da turma");
          const overallGlobal =
            typeof usuarioData?.overall === "number"
              ? usuarioData.overall
              : 65;
          const overallTurma =
            typeof data.overall === "number" ? data.overall : overallGlobal;

          return {
            id: docSnapshot.id,
            ...data,
            usuario: usuarioData,
            nome,
            email: usuarioData?.email || "",
            overallGlobal,
            overallTurma,
          };
        })
      );

      const disponiveis = membros
        .filter(
          (membro) =>
            !membershipsEmUso.has(membro.id) &&
            !usuariosEmUso.has(membro.id_usuario)
        )
        .sort((a, b) => a.nome.localeCompare(b.nome));
      setMembrosDisponiveis(disponiveis);
    } catch (error) {
      console.error("Erro ao carregar integrantes extras", error);
      setErroMembros("Não foi possível carregar os integrantes da turma agora.");
    } finally {
      setCarregandoMembros(false);
    }
  }, [turmaId]);

  useEffect(() => {
    if (!turmaId) {
      setMembrosDisponiveis([]);
      return;
    }
    carregarMembrosDisponiveis();
  }, [turmaId, carregarMembrosDisponiveis]);

  // Função para adicionar jogador à lista de reservas
  const handleAddJogadorReserva = async () => {
    if (!nomeJogador.trim()) {
      alert("Digite o apelido do convidado");
      return;
    }

    try {
      const novoId = await getProximoIdJogador(); // Obtém o próximo ID disponível
      const novoJogador = {
        id: novoId,
        id_fixo: novoId,
        nome: nomeJogador.trim(),
        email: "",
        id_usuario: null,
        id_membro_turma: null,
        is_convidado: true,
        gols: 0,
        assistencias: 0,
        vitorias: 0,
        empates: 0,
        derrotas: 0,
        qt_partidas: 0,
        jogosSemTomarGol: 0,
        influenciaVitoria: 0,
        desempenhoAcumulado: 0,
        overallGlobal: 65,
        overallTurma: 65,
      };

      // Adiciona o jogador no registro principal e na lista de reservas
      await addJogador(novoJogador);
      await addJogadorAosReservas(novoJogador);

      // Atualiza a lista de reservas no componente pai
      if (typeof onReservasAtualizadas === "function") {
        await onReservasAtualizadas();
      }

      // Limpa o campo de input após a adição
      setNomeJogador("");
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Erro ao adicionar convidado", error);
      alert("Não foi possível adicionar o convidado agora.");
    }
  };

  const handleAddMembroTurma = async (membroId) => {
    if (!turmaId) return;
    const membro = membrosDisponiveis.find((item) => item.id === membroId);
    if (!membro) {
      setErroMembros("Selecione um integrante disponível da turma.");
      return;
    }
    setAdicionandoMembroId(membroId);
    setErroMembros("");
    try {
      const novoId = await getProximoIdJogador();
      const novoJogador = {
        id: novoId,
        id_fixo: novoId,
        nome: membro.nome,
        email: membro.email || "",
        id_usuario: membro.id_usuario || null,
        id_membro_turma: membro.id,
        is_convidado: false,
        gols: 0,
        assistencias: 0,
        vitorias: 0,
        empates: 0,
        derrotas: 0,
        qt_partidas: 0,
        jogosSemTomarGol: 0,
        influenciaVitoria: 0,
        desempenhoAcumulado: 0,
        overallGlobal:
          typeof membro.overallGlobal === "number"
            ? membro.overallGlobal
            : 65,
        overallTurma:
          typeof membro.overallTurma === "number"
            ? membro.overallTurma
            : typeof membro.overallGlobal === "number"
              ? membro.overallGlobal
              : 65,
      };
      await addJogador(novoJogador);
      await addJogadorAosReservas(novoJogador);
      if (typeof onReservasAtualizadas === "function") {
        await onReservasAtualizadas();
      }
      setMembrosDisponiveis((prev) =>
        prev.filter((item) => item.id !== membro.id)
      );
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Erro ao adicionar integrante da turma", error);
      setErroMembros("Não foi possível incluir este integrante agora.");
    } finally {
      setAdicionandoMembroId(null);
    }
  };

  const handleOpenEditarJogador = (id) => {
    const jogador = reservasAtualizados.find((item) => item.id === id);
    if (!jogador) return;
    setJogadorSelecionado(jogador);
    setIsModalOpen(true);
  };

  // Função para excluir o jogador da tabela proxReservas
  const handleDeleteJogador = async () => {
    if (!jogadorSelecionado) return;
    await deleteJogador(jogadorSelecionado.id); // Exclui o jogador com o ID selecionado
    onReservasAtualizadas(); // Atualiza a lista de reservas no componente pai
    setIsModalOpen(false); // Fecha o modal
  };

  const handleSubstituirJogadorReserva = async (id) => {
    const jogador = reservasAtualizados.find((jogador) => jogador.id === id);
    setJogadorSelecionado({ ...jogador, time: "proxReservas" }); // Marca que o jogador pertence ao time2
    setIsModalSubOpen(true);
  };

  const substituirJogador = async (jogadorId, reservaId, time) => {
    const sucesso = await substituirJogadorNoDB(jogadorId, reservaId, time);

    if (sucesso) {
      setIsModalSubOpen(false); // Fecha o modal após a operação
      onReservasAtualizadas();
    } else {
      alert("Ocorreu um erro ao tentar substituir o jogador.");
    }
  };

  return (
    <>
      <label className="label-app">| Próximo time</label>
      <div className="proximo-time-area">
        <div className="proximo-time">
          {reservasAtualizados
            .filter(
              (jogador, idx, arr) =>
                arr.findIndex((j) => j.id === jogador.id) === idx
            ) // Remove duplicados
            .map((jogador, index) => (
              <div key={jogador.id} className="proximo-time-jogador">
                <div>
                  <span className="index-reserva">{index + 1} -</span>{" "}
                  {jogador.nome}
                </div>
                <div className="opcoes-reserva">
                  <button onClick={() => handleOpenEditarJogador(jogador.id)}>
                    Editar
                  </button>
                  <button
                    onClick={() => handleSubstituirJogadorReserva(jogador.id)}
                  >
                    <svg
                      width="15"
                      viewBox="0 0 69 80"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M67.6306 25.1213C68.8022 23.9497 68.8022 22.0503 67.6306 20.8787L48.5387 1.7868C47.3672 0.615224 45.4677 0.615224 44.2961 1.7868C43.1245 2.95837 43.1245 4.85786 44.2961 6.02944L61.2667 23L44.2961 39.9706C43.1245 41.1421 43.1245 43.0416 44.2961 44.2132C45.4677 45.3848 47.3672 45.3848 48.5387 44.2132L67.6306 25.1213ZM11 26H65.5093V20H11V26Z"
                        fill="#000"
                      />
                      <path
                        d="M0.874026 55.3787C-0.297547 56.5503 -0.297547 58.4497 0.874026 59.6213L19.9659 78.7132C21.1375 79.8848 23.037 79.8848 24.2085 78.7132C25.3801 77.5416 25.3801 75.6421 24.2085 74.4706L7.23799 57.5L24.2085 40.5294C25.3801 39.3579 25.3801 37.4584 24.2085 36.2868C23.037 35.1152 21.1375 35.1152 19.9659 36.2868L0.874026 55.3787ZM57.5046 54.5L2.99535 54.5V60.5L57.5046 60.5V54.5Z"
                        fill="#000"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>

      <button
        type="button"
        className="reservas-add-trigger"
        onClick={() => setIsAddModalOpen(true)}
      >
        + Adicionar jogador
      </button>

      {isAddModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content reservas-add-modal">
            <div className="modal-content-top">
              <strong>Adicionar jogador</strong>
              <button
                className="modal-cancel"
                onClick={() => setIsAddModalOpen(false)}
              >
                X
              </button>
            </div>
            <div className="reservas-modal-body">
              {turmaId && (
                <div className="reservas-modal-section">
                  <div className="reservas-modal-header">
                    <span>Integrante da turma</span>
                    <small>
                      {carregandoMembros
                        ? "Buscando integrantes..."
                        : membrosDisponiveis.length > 0
                          ? `${membrosDisponiveis.length} disponíveis`
                          : "Todos já estão no jogo"}
                    </small>
                  </div>
                  <div className="reservas-membros-list">
                    {carregandoMembros && (
                      <div className="reservas-membro-item muted">Carregando...</div>
                    )}
                    {!carregandoMembros && membrosDisponiveis.length === 0 && (
                      <div className="reservas-membro-item muted">
                        Todos os integrantes já estão no jogo.
                      </div>
                    )}
                    {!carregandoMembros &&
                      membrosDisponiveis.map((membro) => (
                        <div key={membro.id} className="reservas-membro-item">
                          <div className="reservas-membro-info">
                            <strong>{membro.nome}</strong>
                          </div>
                          <button
                            className="reservas-add-btn inline"
                            type="button"
                            onClick={() => handleAddMembroTurma(membro.id)}
                            disabled={Boolean(adicionandoMembroId)}
                          >
                            {adicionandoMembroId === membro.id ? "..." : "+"}
                          </button>
                        </div>
                      ))}
                  </div>
                  <p className="reservas-helper">
                    Integrantes entram como reserva e contam para ranking e relatório.
                  </p>
                  {erroMembros && (
                    <p className="reservas-error" role="alert">
                      {erroMembros}
                    </p>
                  )}
                </div>
              )}

              <div className="reservas-modal-section">
                <div className="reservas-modal-header">
                  <span>Convidado</span>
                  <small>Não impacta ranking, mas aparece no relatório.</small>
                </div>
                <div className="reservas-modal-row">
                  <input
                    className="input-app"
                    type="text"
                    name="nome-jogador"
                    id="nome-jogador"
                    placeholder="Apelido do convidado"
                    value={nomeJogador}
                    onChange={(e) => setNomeJogador(e.target.value)}
                  />
                  <button id="add-convidado" onClick={handleAddJogadorReserva}>
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edição do jogador (apenas remoção disponível) */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-content-top">
              <strong>Editar jogador</strong>
              <button
                className="modal-cancel"
                onClick={() => setIsModalOpen(false)}
              >
                X
              </button>
            </div>
            <div className="modal-content-body">
              <p>
                <strong>{jogadorSelecionado?.nome}</strong>
              </p>
              <p>Remover este jogador do próximo time? As estatísticas já registradas serão mantidas.</p>
              <div className="modal-content-bottom">
                <button id="deletar-jogador" onClick={handleDeleteJogador}>
                  Remover do jogo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de substituição Jogador */}
      {isModalSubOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-content-top">
              <strong>Substituir Jogador</strong>
              <button
                className="modal-cancel"
                onClick={() => setIsModalSubOpen(false)}
              >
                X
              </button>
            </div>
            <div className="modal-content-body">
              <p>
                <strong>{jogadorSelecionado.nome}</strong>{" "}
              </p>

              <div className="area-lista-reserva">
                {reservasAtualizados.map((reserva) => (
                  <div key={reserva.id} className="lista-reservas-sub">
                    {reserva.nome} {/* Exibir o nome do reserva */}
                    <button
                      className="reservas-sub"
                      onClick={() =>
                        substituirJogador(
                          jogadorSelecionado.id,
                          reserva.id,
                          jogadorSelecionado.time
                        )
                      }
                    >
                      <svg
                        width="15"
                        viewBox="0 0 69 80"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M67.6306 25.1213C68.8022 23.9497 68.8022 22.0503 67.6306 20.8787L48.5387 1.7868C47.3672 0.615224 45.4677 0.615224 44.2961 1.7868C43.1245 2.95837 43.1245 4.85786 44.2961 6.02944L61.2667 23L44.2961 39.9706C43.1245 41.1421 43.1245 43.0416 44.2961 44.2132C45.4677 45.3848 47.3672 45.3848 48.5387 44.2132L67.6306 25.1213ZM11 26H65.5093V20H11V26Z"
                          fill="#FFF"
                        />
                        <path
                          d="M0.874026 55.3787C-0.297547 56.5503 -0.297547 58.4497 0.874026 59.6213L19.9659 78.7132C21.1375 79.8848 23.037 79.8848 24.2085 78.7132C25.3801 77.5416 25.3801 75.6421 24.2085 74.4706L7.23799 57.5L24.2085 40.5294C25.3801 39.3579 25.3801 37.4584 24.2085 36.2868C23.037 35.1152 21.1375 35.1152 19.9659 36.2868L0.874026 55.3787ZM57.5046 54.5L2.99535 54.5V60.5L57.5046 60.5V54.5Z"
                          fill="#FFF"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListaReservas;
